/**
 * Global 404 Not Found Page
 *
 * This page is shown when a route doesn't exist.
 * IMPORTANT: Logs 404 errors so they appear in /admin/errors dashboard.
 *
 * Common causes:
 * - Typo in URL
 * - Deleted/moved page
 * - OAuth callback redirecting to wrong URL (like the Meta Ads bug)
 */

'use client';

import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { GlassButton } from '@/components/ui/glass-button';
import { Home, ArrowLeft } from 'lucide-react';
import { logClientError } from '@/lib/utils/error-logger-client';

export default function NotFound() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Log 404 errors so they appear in /admin/errors
  useEffect(() => {
    const fullUrl = `${pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

    logClientError('NotFound.404', new Error(`404 - Page not found: ${fullUrl}`), {
      pathname,
      searchParams: Object.fromEntries(searchParams.entries()),
      referrer: typeof document !== 'undefined' ? document.referrer : 'unknown',
    });
  }, [pathname, searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-light via-white to-brand-accent/10 dark:from-gray-900 dark:via-gray-800 dark:to-brand-primary/10">
      <div className="text-center space-y-6 p-8">
        <div className="space-y-2">
          <h1 className="text-8xl font-bold bg-gradient-to-r from-brand-primary to-brand-accent bg-clip-text text-transparent">
            404
          </h1>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Page Not Found</h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-md">
            The page you&apos;re looking for doesn&apos;t exist or has been moved.
          </p>
        </div>

        <div className="flex items-center justify-center gap-4">
          <Link href="/">
            <GlassButton variant="default" size="md">
              <Home className="h-4 w-4 mr-2" />
              Go Home
            </GlassButton>
          </Link>
          <GlassButton
            variant="default"
            size="md"
            // @router-back-ok reason=404-page-has-no-contextual-parent-browser-back-is-the-contract
            onClick={() => window.history.back()}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go Back
          </GlassButton>
        </div>

        {/* Show the attempted URL for debugging */}
        <p className="text-xs text-gray-500 dark:text-gray-500 font-mono">Attempted: {pathname}</p>
      </div>
    </div>
  );
}
